import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { authApi, projectsApi, projectPlansApi, calendarApi, filesApi } from '../services/api'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AdminSidebar from '../components/AdminSidebar'
import Modal from '../components/Modal'
import { ArrowLeftIcon, CalendarIcon, EditIcon, TrashIcon, PlusIcon, DragIcon } from '../components/Icons'
import { useToast } from '../components/ToastContainer'
import '../styles/ElisePlanDetail.css'

// Sortable Milestone Card
function SortableMilestoneCard({ milestone, index, onToggleComplete, onEdit, onSchedule, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: milestone.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`milestone-card ${milestone.completed ? 'completed' : ''}`}
    >
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
      >
        <DragIcon size={18} />
      </div>
      <div
        className="milestone-checkbox"
        onClick={() => onToggleComplete(index)}
      >
        {milestone.completed && (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div className="milestone-content" onClick={() => onToggleComplete(index)}>
        <h4>{milestone.title}</h4>
        {milestone.description && (
          <p>{milestone.description}</p>
        )}
        {milestone.target_date && (
          <span className="milestone-date">
            Target: {new Date(milestone.target_date).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="milestone-actions">
        <button
          className="action-btn"
          onClick={(e) => onEdit(index, e)}
          title="Edit milestone"
        >
          <EditIcon size={16} />
        </button>
        <button
          className="action-btn schedule"
          onClick={(e) => onSchedule(milestone, e)}
          title="Schedule on calendar"
        >
          <CalendarIcon size={16} />
        </button>
        <button
          className="action-btn danger"
          onClick={(e) => onDelete(index, e)}
          title="Delete milestone"
        >
          <TrashIcon size={16} />
        </button>
      </div>
    </div>
  )
}

// Sortable Task Card
function SortableTaskCard({ task, index, onToggleComplete, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`task-card ${task.status}`}
    >
      <div className="task-header">
        <div
          className="drag-handle"
          {...attributes}
          {...listeners}
        >
          <DragIcon size={18} />
        </div>
        <label className="task-checkbox">
          <input
            type="checkbox"
            checked={task.status === 'completed'}
            onChange={() => onToggleComplete(index)}
          />
          <span className="checkmark"></span>
        </label>
        <span className={`priority-badge ${task.priority}`}>
          {task.priority}
        </span>
      </div>
      <div className="task-body">
        <h4 className={task.status === 'completed' ? 'strikethrough' : ''}>
          {task.title}
        </h4>
        {task.description && (
          <p className="task-description">{task.description}</p>
        )}
        {task.due_date && (
          <span className="task-due">
            Due: {new Date(task.due_date).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="task-actions">
        <button
          className="action-btn"
          onClick={(e) => onEdit(index, e)}
          title="Edit task"
        >
          <EditIcon size={16} />
        </button>
        <button
          className="action-btn danger"
          onClick={(e) => onDelete(index, e)}
          title="Delete task"
        >
          <TrashIcon size={16} />
        </button>
      </div>
    </div>
  )
}

function ElisePlanDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [plan, setPlan] = useState(null)
  const [project, setProject] = useState(null)
  const [editingNotes, setEditingNotes] = useState(false)
  const [notesValue, setNotesValue] = useState('')
  const [savingNotes, setSavingNotes] = useState(false)

  // Title editing
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState('')
  const [savingTitle, setSavingTitle] = useState(false)

  // Description editing
  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionValue, setDescriptionValue] = useState('')
  const [savingDescription, setSavingDescription] = useState(false)

  // Objectives editing
  const [editingObjectives, setEditingObjectives] = useState(false)
  const [objectivesValue, setObjectivesValue] = useState([])
  const [savingObjectives, setSavingObjectives] = useState(false)

  // Schedule milestone modal
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [selectedMilestone, setSelectedMilestone] = useState(null)
  const [scheduleForm, setScheduleForm] = useState({
    event_date: '',
    event_time: '09:00',
    duration_hours: 1
  })
  const [scheduling, setScheduling] = useState(false)

  // Milestone edit/add modal
  const [showMilestoneModal, setShowMilestoneModal] = useState(false)
  const [editingMilestoneIndex, setEditingMilestoneIndex] = useState(null) // null = adding new
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    description: '',
    target_date: ''
  })
  const [savingMilestone, setSavingMilestone] = useState(false)

  // Task edit/add modal
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [editingTaskIndex, setEditingTaskIndex] = useState(null) // null = adding new
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: ''
  })
  const [savingTask, setSavingTask] = useState(false)

  // File upload
  const [uploadingFile, setUploadingFile] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef(null)

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        navigate('/admin/login')
        return
      }

      try {
        const response = await authApi.verify()
        setUser(response.data.user)
        loadPlanData()
      } catch (error) {
        console.error('Auth verification failed:', error)
        localStorage.removeItem('auth_token')
        navigate('/admin/login')
      }
    }

    checkAuth()
  }, [navigate, id])

  const loadPlanData = async () => {
    try {
      const planResponse = await projectPlansApi.getById(id)
      setPlan(planResponse.data)

      if (planResponse.data.project_id) {
        try {
          const projectResponse = await projectsApi.getById(planResponse.data.project_id)
          setProject(projectResponse.data)
        } catch (error) {
          console.error('Error loading project:', error)
        }
      }
    } catch (error) {
      console.error('Error loading plan:', error)
      toast.error('Error loading project plan')
      navigate('/elise')
    } finally {
      setLoading(false)
    }
  }

  const toggleTaskComplete = async (taskIndex) => {
    try {
      const updatedPlan = { ...plan }
      const task = updatedPlan.tasks[taskIndex]
      task.status = task.status === 'completed' ? 'pending' : 'completed'
      task.completed_at = task.status === 'completed' ? new Date().toISOString() : null

      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
    } catch (error) {
      console.error('Error updating task:', error)
      toast.error('Error updating task')
    }
  }

  const toggleMilestoneComplete = async (milestoneIndex) => {
    try {
      const updatedPlan = { ...plan }
      const milestone = updatedPlan.milestones[milestoneIndex]
      milestone.completed = !milestone.completed
      milestone.completed_at = milestone.completed ? new Date().toISOString() : null

      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
    } catch (error) {
      console.error('Error updating milestone:', error)
      toast.error('Error updating milestone')
    }
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this project plan?')) {
      try {
        await projectPlansApi.delete(plan.id)
        toast.success('Project plan deleted')
        navigate('/elise')
      } catch (error) {
        console.error('Error deleting plan:', error)
        toast.error('Error deleting project plan')
      }
    }
  }

  const handleEditNotes = () => {
    setNotesValue(plan.notes || '')
    setEditingNotes(true)
  }

  const handleSaveNotes = async () => {
    setSavingNotes(true)
    try {
      const updatedPlan = { ...plan, notes: notesValue }
      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      setEditingNotes(false)
      toast.success('Notes saved')
    } catch (error) {
      console.error('Error saving notes:', error)
      toast.error('Error saving notes')
    } finally {
      setSavingNotes(false)
    }
  }

  const handleCancelEditNotes = () => {
    setEditingNotes(false)
    setNotesValue('')
  }

  // ============== TITLE EDITING ==============
  const handleEditTitle = () => {
    setTitleValue(plan.title || '')
    setEditingTitle(true)
  }

  const handleSaveTitle = async () => {
    if (!titleValue.trim()) {
      toast.error('Title is required')
      return
    }
    setSavingTitle(true)
    try {
      const updatedPlan = { ...plan, title: titleValue.trim() }
      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      setEditingTitle(false)
      toast.success('Title saved')
    } catch (error) {
      console.error('Error saving title:', error)
      toast.error('Error saving title')
    } finally {
      setSavingTitle(false)
    }
  }

  const handleCancelEditTitle = () => {
    setEditingTitle(false)
    setTitleValue('')
  }

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveTitle()
    } else if (e.key === 'Escape') {
      handleCancelEditTitle()
    }
  }

  // ============== DESCRIPTION EDITING ==============
  const handleEditDescription = () => {
    setDescriptionValue(plan.description || '')
    setEditingDescription(true)
  }

  const handleSaveDescription = async () => {
    setSavingDescription(true)
    try {
      const updatedPlan = { ...plan, description: descriptionValue }
      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      setEditingDescription(false)
      toast.success('Description saved')
    } catch (error) {
      console.error('Error saving description:', error)
      toast.error('Error saving description')
    } finally {
      setSavingDescription(false)
    }
  }

  const handleCancelEditDescription = () => {
    setEditingDescription(false)
    setDescriptionValue('')
  }

  // ============== OBJECTIVES EDITING ==============
  const handleEditObjectives = () => {
    setObjectivesValue(plan.objectives?.length > 0 ? [...plan.objectives] : [''])
    setEditingObjectives(true)
  }

  const handleObjectiveChange = (index, value) => {
    const updated = [...objectivesValue]
    updated[index] = value
    setObjectivesValue(updated)
  }

  const handleAddObjective = () => {
    setObjectivesValue([...objectivesValue, ''])
  }

  const handleRemoveObjective = (index) => {
    const updated = objectivesValue.filter((_, i) => i !== index)
    setObjectivesValue(updated.length > 0 ? updated : [''])
  }

  const handleSaveObjectives = async () => {
    setSavingObjectives(true)
    try {
      // Filter out empty objectives
      const filteredObjectives = objectivesValue.filter(obj => obj.trim() !== '')
      const updatedPlan = { ...plan, objectives: filteredObjectives }
      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      setEditingObjectives(false)
      toast.success('Objectives saved')
    } catch (error) {
      console.error('Error saving objectives:', error)
      toast.error('Error saving objectives')
    } finally {
      setSavingObjectives(false)
    }
  }

  const handleCancelEditObjectives = () => {
    setEditingObjectives(false)
    setObjectivesValue([])
  }

  // ============== FILE UPLOAD ==============
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setUploadingFile(true)
    setUploadProgress(0)

    try {
      const uploadedFiles = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const response = await filesApi.upload(file, (progressEvent) => {
          const percentCompleted = Math.round(
            ((i + (progressEvent.loaded / progressEvent.total)) / files.length) * 100
          )
          setUploadProgress(percentCompleted)
        })

        if (response.data.url) {
          uploadedFiles.push({
            name: file.name,
            url: response.data.url
          })
        }
      }

      if (uploadedFiles.length > 0) {
        const updatedPlan = {
          ...plan,
          reference_files: [...(plan.reference_files || []), ...uploadedFiles]
        }
        await projectPlansApi.update(plan.id, updatedPlan)
        setPlan(updatedPlan)
        toast.success(`Uploaded ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}`)
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Error uploading file')
    } finally {
      setUploadingFile(false)
      setUploadProgress(0)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDeleteFile = async (fileUrl, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return

    try {
      await filesApi.delete(fileUrl)
      const updatedPlan = {
        ...plan,
        reference_files: (plan.reference_files || []).filter(f => f.url !== fileUrl)
      }
      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      toast.success('File deleted')
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error('Error deleting file')
    }
  }

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image'
    if (['stl', 'obj', 'step', '3mf'].includes(ext)) return '3d'
    if (['pdf'].includes(ext)) return 'pdf'
    if (['dxf', 'dwg', 'ai', 'eps'].includes(ext)) return 'cad'
    return 'file'
  }

  const openScheduleModal = (milestone, e) => {
    e.stopPropagation()
    setSelectedMilestone(milestone)
    setScheduleForm({
      event_date: milestone.target_date ? milestone.target_date.split('T')[0] : '',
      event_time: '09:00',
      duration_hours: 1
    })
    setShowScheduleModal(true)
  }

  const handleScheduleMilestone = async (e) => {
    e.preventDefault()
    if (!selectedMilestone || !scheduleForm.event_date) return

    setScheduling(true)
    try {
      const response = await calendarApi.createFromMilestone({
        plan_id: plan.id,
        milestone_id: selectedMilestone.id,
        event_date: scheduleForm.event_date,
        event_time: scheduleForm.event_time,
        duration_hours: scheduleForm.duration_hours
      })

      if (response.data.success) {
        toast.success(`Scheduled "${selectedMilestone.title}" on calendar`)
        setShowScheduleModal(false)
        setSelectedMilestone(null)
      } else {
        toast.error(response.data.message || 'Failed to schedule milestone')
      }
    } catch (error) {
      console.error('Error scheduling milestone:', error)
      toast.error(error.response?.data?.detail || 'Failed to schedule milestone')
    } finally {
      setScheduling(false)
    }
  }

  // ============== DRAG AND DROP ==============
  const handleMilestoneDragEnd = async (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = plan.milestones.findIndex(m => m.id === active.id)
    const newIndex = plan.milestones.findIndex(m => m.id === over.id)

    const newMilestones = arrayMove(plan.milestones, oldIndex, newIndex)
    const updatedPlan = { ...plan, milestones: newMilestones }

    setPlan(updatedPlan)

    try {
      await projectPlansApi.update(plan.id, updatedPlan)
    } catch (error) {
      console.error('Error reordering milestones:', error)
      toast.error('Error saving milestone order')
      loadPlanData() // Reload on error
    }
  }

  const handleTaskDragEnd = async (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = plan.tasks.findIndex(t => t.id === active.id)
    const newIndex = plan.tasks.findIndex(t => t.id === over.id)

    const newTasks = arrayMove(plan.tasks, oldIndex, newIndex)
    const updatedPlan = { ...plan, tasks: newTasks }

    setPlan(updatedPlan)

    try {
      await projectPlansApi.update(plan.id, updatedPlan)
    } catch (error) {
      console.error('Error reordering tasks:', error)
      toast.error('Error saving task order')
      loadPlanData() // Reload on error
    }
  }

  // ============== MILESTONE CRUD ==============
  const openAddMilestone = () => {
    setEditingMilestoneIndex(null)
    setMilestoneForm({
      title: '',
      description: '',
      target_date: ''
    })
    setShowMilestoneModal(true)
  }

  const openEditMilestone = (index, e) => {
    e.stopPropagation()
    const milestone = plan.milestones[index]
    setEditingMilestoneIndex(index)
    setMilestoneForm({
      title: milestone.title || '',
      description: milestone.description || '',
      target_date: milestone.target_date ? milestone.target_date.split('T')[0] : ''
    })
    setShowMilestoneModal(true)
  }

  const handleSaveMilestone = async (e) => {
    e.preventDefault()
    if (!milestoneForm.title.trim()) {
      toast.error('Milestone title is required')
      return
    }

    setSavingMilestone(true)
    try {
      const updatedPlan = { ...plan }
      const milestoneData = {
        id: editingMilestoneIndex !== null
          ? plan.milestones[editingMilestoneIndex].id
          : `milestone-${Date.now()}`,
        title: milestoneForm.title.trim(),
        description: milestoneForm.description.trim(),
        target_date: milestoneForm.target_date || null,
        completed: editingMilestoneIndex !== null
          ? plan.milestones[editingMilestoneIndex].completed
          : false,
        completed_at: editingMilestoneIndex !== null
          ? plan.milestones[editingMilestoneIndex].completed_at
          : null
      }

      if (editingMilestoneIndex !== null) {
        updatedPlan.milestones[editingMilestoneIndex] = milestoneData
      } else {
        updatedPlan.milestones.push(milestoneData)
      }

      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      setShowMilestoneModal(false)
      toast.success(editingMilestoneIndex !== null ? 'Milestone updated' : 'Milestone added')
    } catch (error) {
      console.error('Error saving milestone:', error)
      toast.error('Error saving milestone')
    } finally {
      setSavingMilestone(false)
    }
  }

  const handleDeleteMilestone = async (index, e) => {
    e.stopPropagation()
    const milestone = plan.milestones[index]
    if (!window.confirm(`Delete milestone "${milestone.title}"?`)) return

    try {
      const updatedPlan = { ...plan }
      updatedPlan.milestones.splice(index, 1)
      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      toast.success('Milestone deleted')
    } catch (error) {
      console.error('Error deleting milestone:', error)
      toast.error('Error deleting milestone')
    }
  }

  // ============== TASK CRUD ==============
  const openAddTask = () => {
    setEditingTaskIndex(null)
    setTaskForm({
      title: '',
      description: '',
      priority: 'medium',
      due_date: ''
    })
    setShowTaskModal(true)
  }

  const openEditTask = (index, e) => {
    e.stopPropagation()
    const task = plan.tasks[index]
    setEditingTaskIndex(index)
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      due_date: task.due_date ? task.due_date.split('T')[0] : ''
    })
    setShowTaskModal(true)
  }

  const handleSaveTask = async (e) => {
    e.preventDefault()
    if (!taskForm.title.trim()) {
      toast.error('Task title is required')
      return
    }

    setSavingTask(true)
    try {
      const updatedPlan = { ...plan }
      const taskData = {
        id: editingTaskIndex !== null
          ? plan.tasks[editingTaskIndex].id
          : `task-${Date.now()}`,
        title: taskForm.title.trim(),
        description: taskForm.description.trim(),
        priority: taskForm.priority,
        due_date: taskForm.due_date || null,
        status: editingTaskIndex !== null
          ? plan.tasks[editingTaskIndex].status
          : 'pending',
        completed_at: editingTaskIndex !== null
          ? plan.tasks[editingTaskIndex].completed_at
          : null
      }

      if (editingTaskIndex !== null) {
        updatedPlan.tasks[editingTaskIndex] = taskData
      } else {
        updatedPlan.tasks.push(taskData)
      }

      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      setShowTaskModal(false)
      toast.success(editingTaskIndex !== null ? 'Task updated' : 'Task added')
    } catch (error) {
      console.error('Error saving task:', error)
      toast.error('Error saving task')
    } finally {
      setSavingTask(false)
    }
  }

  const handleDeleteTask = async (index, e) => {
    e.stopPropagation()
    const task = plan.tasks[index]
    if (!window.confirm(`Delete task "${task.title}"?`)) return

    try {
      const updatedPlan = { ...plan }
      updatedPlan.tasks.splice(index, 1)
      await projectPlansApi.update(plan.id, updatedPlan)
      setPlan(updatedPlan)
      toast.success('Task deleted')
    } catch (error) {
      console.error('Error deleting task:', error)
      toast.error('Error deleting task')
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  if (!user || !plan) {
    return null
  }

  const completedTasks = plan.tasks.filter(t => t.status === 'completed').length
  const totalTasks = plan.tasks.length
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0

  const completedMilestones = plan.milestones.filter(m => m.completed).length
  const totalMilestones = plan.milestones.length

  const backgroundImage = project?.images?.[0] || null

  return (
    <div className="admin-panel">
      <AdminSidebar currentPage="elise" />

      <div className="admin-content">
        <div className="plan-detail-container">
          {/* Header with back button */}
          <div className="plan-detail-header">
            <Link to="/elise" className="back-button">
              <ArrowLeftIcon size={20} />
              <span>Back to Plans</span>
            </Link>
          </div>

          {/* Hero section with background */}
          <div
            className="plan-detail-hero"
            style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {}}
          >
            <div className="hero-overlay">
              <div className="title-section">
                {editingTitle ? (
                  <div className="title-edit">
                    <input
                      type="text"
                      value={titleValue}
                      onChange={(e) => setTitleValue(e.target.value)}
                      onKeyDown={handleTitleKeyDown}
                      placeholder="Enter plan title..."
                      className="title-input"
                      autoFocus
                    />
                    <div className="title-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSaveTitle}
                        disabled={savingTitle}
                      >
                        {savingTitle ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleCancelEditTitle}
                        disabled={savingTitle}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="title-display">
                    <h1>{plan.title}</h1>
                    <button
                      className="title-edit-btn"
                      onClick={handleEditTitle}
                      title="Edit title"
                    >
                      <EditIcon size={20} />
                    </button>
                  </div>
                )}
              </div>
              {project && <p className="project-link">{project.title}</p>}

              <div className="progress-section">
                <div className="progress-stats">
                  <span>{completedTasks}/{totalTasks} tasks completed</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="progress-bar-large">
                  <div
                    className="progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="plan-detail-content">
            {/* Description */}
            <section className="plan-section">
              <div className="section-header">
                <h2>Description</h2>
                {!editingDescription && (
                  <button className="btn btn-secondary btn-sm" onClick={handleEditDescription}>
                    Edit
                  </button>
                )}
              </div>
              {editingDescription ? (
                <div className="description-edit">
                  <textarea
                    value={descriptionValue}
                    onChange={(e) => setDescriptionValue(e.target.value)}
                    placeholder="Add a description for this project plan..."
                    rows={4}
                    className="description-textarea"
                  />
                  <div className="notes-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveDescription}
                      disabled={savingDescription}
                    >
                      {savingDescription ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleCancelEditDescription}
                      disabled={savingDescription}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : plan.description ? (
                <p className="description-text">{plan.description}</p>
              ) : (
                <div className="empty-state">
                  <p>No description yet. Click Edit to add one.</p>
                </div>
              )}
            </section>

            {/* Objectives */}
            <section className="plan-section">
              <div className="section-header">
                <h2>Objectives</h2>
                {!editingObjectives && (
                  <button className="btn btn-secondary btn-sm" onClick={handleEditObjectives}>
                    Edit
                  </button>
                )}
              </div>
              {editingObjectives ? (
                <div className="objectives-edit">
                  <div className="objectives-inputs">
                    {objectivesValue.map((obj, idx) => (
                      <div key={idx} className="objective-input-row">
                        <input
                          type="text"
                          value={obj}
                          onChange={(e) => handleObjectiveChange(idx, e.target.value)}
                          placeholder={`Objective ${idx + 1}`}
                          className="objective-input"
                        />
                        <button
                          type="button"
                          className="btn btn-icon btn-danger"
                          onClick={() => handleRemoveObjective(idx)}
                          title="Remove objective"
                        >
                          <TrashIcon size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm add-objective-btn"
                    onClick={handleAddObjective}
                  >
                    <PlusIcon size={16} />
                    Add Objective
                  </button>
                  <div className="notes-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveObjectives}
                      disabled={savingObjectives}
                    >
                      {savingObjectives ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleCancelEditObjectives}
                      disabled={savingObjectives}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : plan.objectives?.length > 0 ? (
                <ul className="objectives-list">
                  {plan.objectives.map((obj, idx) => (
                    <li key={idx}>{obj}</li>
                  ))}
                </ul>
              ) : (
                <div className="empty-state">
                  <p>No objectives yet. Click Edit to add some.</p>
                </div>
              )}
            </section>

            {/* Milestones */}
            <section className="plan-section">
              <div className="section-header">
                <h2>
                  Milestones
                  {totalMilestones > 0 && (
                    <span className="section-count">
                      {completedMilestones}/{totalMilestones}
                    </span>
                  )}
                </h2>
                <button className="btn btn-primary btn-sm" onClick={openAddMilestone}>
                  <PlusIcon size={16} />
                  Add Milestone
                </button>
              </div>
              {plan.milestones.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleMilestoneDragEnd}
                >
                  <SortableContext
                    items={plan.milestones.map(m => m.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="milestones-list">
                      {plan.milestones.map((milestone, idx) => (
                        <SortableMilestoneCard
                          key={milestone.id}
                          milestone={milestone}
                          index={idx}
                          onToggleComplete={toggleMilestoneComplete}
                          onEdit={openEditMilestone}
                          onSchedule={openScheduleModal}
                          onDelete={handleDeleteMilestone}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="empty-state">
                  <p>No milestones yet. Add milestones to track major progress points.</p>
                </div>
              )}
            </section>

            {/* Tasks */}
            <section className="plan-section">
              <div className="section-header">
                <h2>
                  Tasks
                  {totalTasks > 0 && (
                    <span className="section-count">
                      {completedTasks}/{totalTasks}
                    </span>
                  )}
                </h2>
                <button className="btn btn-primary btn-sm" onClick={openAddTask}>
                  <PlusIcon size={16} />
                  Add Task
                </button>
              </div>
              {plan.tasks.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleTaskDragEnd}
                >
                  <SortableContext
                    items={plan.tasks.map(t => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="tasks-list">
                      {plan.tasks.map((task, idx) => (
                        <SortableTaskCard
                          key={task.id}
                          task={task}
                          index={idx}
                          onToggleComplete={toggleTaskComplete}
                          onEdit={openEditTask}
                          onDelete={handleDeleteTask}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="empty-state">
                  <p>No tasks yet. Add tasks to break down the work into actionable items.</p>
                </div>
              )}
            </section>

            {/* Reference Files */}
            <section className="plan-section">
              <div className="section-header">
                <h2>Reference Files</h2>
                <label className="btn btn-primary btn-sm upload-btn">
                  <PlusIcon size={16} />
                  {uploadingFile ? `Uploading ${uploadProgress}%` : 'Add Files'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,.stl,.obj,.step,.3mf,.pdf,.dxf,.dwg,.ai,.eps"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              {plan.reference_files?.length > 0 ? (
                <div className="reference-files-grid">
                  {plan.reference_files.map((file, idx) => {
                    const fileType = getFileIcon(file.name)
                    const isImage = fileType === 'image'
                    return (
                      <div key={idx} className={`reference-file-card ${fileType}`}>
                        {isImage ? (
                          <a href={file.url} target="_blank" rel="noopener noreferrer" className="file-preview">
                            <img src={file.url} alt={file.name} />
                          </a>
                        ) : (
                          <a href={file.url} target="_blank" rel="noopener noreferrer" className="file-preview file-icon-preview">
                            <div className="file-type-icon">
                              {fileType === '3d' && (
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                                  <path d="M2 17l10 5 10-5" />
                                  <path d="M2 12l10 5 10-5" />
                                </svg>
                              )}
                              {fileType === 'pdf' && (
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                  <path d="M14 2v6h6" />
                                  <path d="M10 9H8v6h2a2 2 0 002-2v-2a2 2 0 00-2-2z" />
                                  <path d="M16 9h-2v6" />
                                </svg>
                              )}
                              {fileType === 'cad' && (
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <rect x="3" y="3" width="18" height="18" rx="2" />
                                  <path d="M7 7h10M7 12h10M7 17h10" />
                                </svg>
                              )}
                              {fileType === 'file' && (
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                  <path d="M14 2v6h6" />
                                </svg>
                              )}
                            </div>
                            <span className="file-extension">.{file.name.split('.').pop()}</span>
                          </a>
                        )}
                        <div className="file-info">
                          <span className="file-name" title={file.name}>{file.name}</span>
                          <button
                            className="file-delete-btn"
                            onClick={() => handleDeleteFile(file.url, file.name)}
                            title="Delete file"
                          >
                            <TrashIcon size={14} />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No reference files yet. Add images, 3D designs, or documents.</p>
                </div>
              )}
            </section>

            {/* Notes */}
            <section className="plan-section">
              <div className="section-header">
                <h2>Notes</h2>
                {!editingNotes && (
                  <button className="btn btn-secondary btn-sm" onClick={handleEditNotes}>
                    Edit
                  </button>
                )}
              </div>
              {editingNotes ? (
                <div className="notes-edit">
                  <textarea
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    placeholder="Add notes about this project plan..."
                    rows={6}
                  />
                  <div className="notes-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveNotes}
                      disabled={savingNotes}
                    >
                      {savingNotes ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleCancelEditNotes}
                      disabled={savingNotes}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="notes-box">
                  {plan.notes || <span className="empty-notes">No notes yet. Click Edit to add some.</span>}
                </div>
              )}
            </section>

            {/* Danger Zone */}
            <section className="plan-section danger-zone">
              <div className="section-header">
                <h2>Danger Zone</h2>
              </div>
              <div className="danger-zone-content">
                <div className="danger-zone-item">
                  <div className="danger-zone-text">
                    <strong>Delete this project plan</strong>
                    <p>Once deleted, this plan and all its data cannot be recovered.</p>
                  </div>
                  <button className="btn btn-danger" onClick={handleDelete}>
                    Delete Plan
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Schedule Milestone Modal */}
      <Modal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false)
          setSelectedMilestone(null)
        }}
        title={`Schedule: ${selectedMilestone?.title || 'Milestone'}`}
      >
        <form onSubmit={handleScheduleMilestone}>
          <div className="form-group">
            <label htmlFor="event_date">Date *</label>
            <input
              type="date"
              id="event_date"
              value={scheduleForm.event_date}
              onChange={(e) => setScheduleForm({ ...scheduleForm, event_date: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="event_time">Time</label>
            <input
              type="time"
              id="event_time"
              value={scheduleForm.event_time}
              onChange={(e) => setScheduleForm({ ...scheduleForm, event_time: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="duration_hours">Duration (hours)</label>
            <select
              id="duration_hours"
              value={scheduleForm.duration_hours}
              onChange={(e) => setScheduleForm({ ...scheduleForm, duration_hours: parseInt(e.target.value) })}
            >
              <option value={1}>1 hour</option>
              <option value={2}>2 hours</option>
              <option value={4}>4 hours</option>
              <option value={8}>Full day (8 hours)</option>
            </select>
          </div>

          <div className="form-actions" style={{ marginTop: '20px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={scheduling || !scheduleForm.event_date}
            >
              {scheduling ? 'Scheduling...' : 'Add to Calendar'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowScheduleModal(false)
                setSelectedMilestone(null)
              }}
              disabled={scheduling}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Milestone Add/Edit Modal */}
      <Modal
        isOpen={showMilestoneModal}
        onClose={() => setShowMilestoneModal(false)}
        title={editingMilestoneIndex !== null ? 'Edit Milestone' : 'Add Milestone'}
      >
        <form onSubmit={handleSaveMilestone}>
          <div className="form-group">
            <label htmlFor="milestone_title">Title *</label>
            <input
              type="text"
              id="milestone_title"
              value={milestoneForm.title}
              onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
              placeholder="Enter milestone title"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="milestone_description">Description</label>
            <textarea
              id="milestone_description"
              value={milestoneForm.description}
              onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="milestone_target_date">Target Date</label>
            <input
              type="date"
              id="milestone_target_date"
              value={milestoneForm.target_date}
              onChange={(e) => setMilestoneForm({ ...milestoneForm, target_date: e.target.value })}
            />
          </div>

          <div className="form-actions" style={{ marginTop: '20px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingMilestone}
            >
              {savingMilestone ? 'Saving...' : (editingMilestoneIndex !== null ? 'Update' : 'Add')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowMilestoneModal(false)}
              disabled={savingMilestone}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Task Add/Edit Modal */}
      <Modal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        title={editingTaskIndex !== null ? 'Edit Task' : 'Add Task'}
      >
        <form onSubmit={handleSaveTask}>
          <div className="form-group">
            <label htmlFor="task_title">Title *</label>
            <input
              type="text"
              id="task_title"
              value={taskForm.title}
              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              placeholder="Enter task title"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="task_description">Description</label>
            <textarea
              id="task_description"
              value={taskForm.description}
              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="task_priority">Priority</label>
            <select
              id="task_priority"
              value={taskForm.priority}
              onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="task_due_date">Due Date</label>
            <input
              type="date"
              id="task_due_date"
              value={taskForm.due_date}
              onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
            />
          </div>

          <div className="form-actions" style={{ marginTop: '20px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={savingTask}
            >
              {savingTask ? 'Saving...' : (editingTaskIndex !== null ? 'Update' : 'Add')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowTaskModal(false)}
              disabled={savingTask}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

export default ElisePlanDetail
